import React from 'react';
import { Access, Role, UserType } from 'src/shared/types';
import { useAppSelector, useAppDispatch } from 'src/store';
import {
    getNewProjectUserFormState,
    getUserManagementGeneralUiState,
} from 'src/app/admin/views/user-management/state/userManagementSelector';
import { NewProjectUserFormState, setFormState } from 'src/app/admin/views/user-management/state/userManagementSlice';
import useAuthService from 'src/shared/hooks/useAuthService';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import UserDetails from 'src/app/admin/views/user-management/components/user-details/UserDetails';
import AssignProjects from 'src/app/admin/views/user-management/components/assign-projects/AssignProjects';
import CustomerRoleCheckboxes from 'src/app/admin/views/user-management/components/assign-admin-roles/CustomerRoleCheckboxes';
import OrganisationalViewerCheckbox from 'src/app/admin/views/user-management/components/assign-admin-roles/OrganisationalViewerCheckbox';
import UserManagementDrawerLayout from 'src/app/admin/views/user-management/components/user-drawer-layout/UserManagementDrawerLayout';
import InfoMessage from 'src/shared/components/info-message/InfoMessage';
import styles from './UserProjectsForm.module.scss';

/** Add-user step 2: admin roles + `AssignProjects` for the customer being managed */
const UserProjectsForm = () => {
    const formState = useAppSelector(getNewProjectUserFormState);
    const dispatch = useAppDispatch();
    const { isCurrentUserSuperAdmin } = useAuthService();
    const { currentCustomerId, currentCustomerProjectIds } = useCustomer();
    const generalUiState = useAppSelector(getUserManagementGeneralUiState);
    const { hasCustomerAdminInUsers } = generalUiState;

    const userRoles = formState?.selectedUser?.customers.find(customer => customer.customerId === currentCustomerId)?.roles;
    //dont show Customer Admin Badge permisions if user is not admin and usertype is internal
    const isCustomerAdmin =
        isCurrentUserSuperAdmin &&
        formState.userType !== UserType.INTERNAL &&
        (Boolean(userRoles?.includes(Role.CUSTOMER_ADMIN)) || formState.roles.includes(Role.CUSTOMER_ADMIN));

    const onFormChange = (fragment: Partial<NewProjectUserFormState['formState']>) => {
        dispatch(
            setFormState({
                ...fragment,
            })
        );
    };

    let userType = UserType.EXTERNAL;
    if (isCurrentUserSuperAdmin && formState.userType === UserType.INTERNAL) {
        userType = UserType.INTERNAL;
    }

    const userDetailsProps = formState.selectedUser
        ? { ...formState.selectedUser, userType: formState.selectedUser.is_internal_user ? UserType.INTERNAL : UserType.EXTERNAL }
        : {
              firstName: formState.firstName,
              lastName: formState.lastName,
              email: formState.email,
              phone: `${formState.phoneCountryCode} ${formState.phone}`,
              ...(userType === UserType.INTERNAL ? { userType } : {}),
              isCustomerAdmin,
              status: 'Active',
              contactInfoClass: styles.userContact,
          };

    // no sites to assign — hide the site-permissions section entirely rather than showing an empty/disabled matrix
    const customerHasSites = (currentCustomerProjectIds?.length ?? 0) > 0;

    return (
        <div className={styles.userProjectForm} data-testid='user-management-add-user-step-two'>
            <div className={styles.stepIndicator}>Step 2 of 2</div>
            <UserManagementDrawerLayout
                variant='stacked'
                sidebar={
                    <div className={styles.sectionContainer}>
                        <div className={styles.section}>
                            <div className={styles.sectionHeading}>Sites and roles</div>
                            <UserDetails {...userDetailsProps} />
                        </div>
                        {isCurrentUserSuperAdmin && (
                            <div className={styles.section} data-testid='user-management-admin-roles-section'>
                                {!hasCustomerAdminInUsers && (
                                    <InfoMessage
                                        className={styles.firstUserInfo}
                                        message='We recommend the first external user for this organisation be a customer admin and/or a project admin, so they can help manage further users and access themselves'
                                    />
                                )}
                                <CustomerRoleCheckboxes
                                    onChange={onFormChange}
                                    roles={formState.roles}
                                    defaultRoles={userRoles}
                                    isInternalUser={userType === UserType.INTERNAL}
                                />
                                {/* Only a brand-new user (no selectedUser) is submitted via createUser, which is
                                    the only mutation that persists Cognito groups (CreateUserInput.groups).
                                    formState.selectedUser -- linking an existing account to this org -- goes
                                    through adminUpdateUser instead, which has no groups field at all. */}
                                {!formState.selectedUser && (
                                    <OrganisationalViewerCheckbox
                                        onChange={onFormChange}
                                        groups={formState.groups}
                                        isInternalUser={userType === UserType.INTERNAL}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                }
                main={
                    <div className={styles.section}>
                        <div className={styles.sectionHeading}>Site permissions</div>
                        {customerHasSites ? (
                            <AssignProjects
                                formState={{ ...formState, is_internal_user: formState.userType === UserType.INTERNAL }}
                                onFormChange={onFormChange}
                                formMode={Access.CREATE}
                                disabled={false}
                            />
                        ) : (
                            <InfoMessage message="This organisation doesn't have any sites yet." />
                        )}
                    </div>
                }
            />
        </div>
    );
};

export default UserProjectsForm;
